import RequirePricingAdminUser from '@/components/auth/RequirePricingAdminUser';
import DateTimeDisplay from '@/components/DateTime';
import {
  Badge,
  Box,
  Button,
  Card,
  Column,
  Table,
  toast,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { PricingSettingStatus, PropertyType as PropertyTypeEnum, propertyTypeLabelsMap } from 'types';

import {
  useDeleteOrganizationPropertyTypePricingMutation,
  useGetOrganizationPropertyTypePricingQuery,
} from '../../../../../services/pricing';
import { TableLoading } from '../../../../TableLoading';

import { CreatePropertyType } from './modals/CreatePropertyType';

interface PropertyTypePricingRow {
  id: string;
  organizationId: string;
  organizationName: string;
  propertyType: PropertyTypeEnum;
  programType: string;
  status: PricingSettingStatus;
  value: { pricePerWatt: number };
  createdBy?: { user?: { id: string; name: string }; date?: string };
  archivedBy?: { user?: { id: string; name: string }; date?: string };
}

const formatPricePerWatt = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / W`;

export const PropertyType = () => {
  const { alias = '', programType = '' } = useParams<{ alias: string; programType: string }>();
  const { isPhone } = useBreakpoint();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const { data, isLoading, isFetching } = useGetOrganizationPropertyTypePricingQuery(
    { alias, programType },
    { skip: !alias || !programType },
  );
  const [deletePropertyTypePricingMutation] = useDeleteOrganizationPropertyTypePricingMutation();

  const propertyTypes = useMemo(() => {
    const list = (data?.data ?? []) as PropertyTypePricingRow[];
    if (list.length === 0) {
      return [];
    }
    const byStatus = list.reduce(
      (acc, row) => {
        acc[row.status] = acc[row.status] || [];
        acc[row.status].push(row);
        return acc;
      },
      {} as Record<string, PropertyTypePricingRow[]>,
    );
    const getCreatedDateMs = (row: PropertyTypePricingRow) =>
      row.createdBy?.date ? new Date(row.createdBy.date).getTime() : 0;
    const sortByCreatedDateDesc = (a: PropertyTypePricingRow, b: PropertyTypePricingRow) =>
      getCreatedDateMs(b) - getCreatedDateMs(a);

    return [
      ...(byStatus[PricingSettingStatus.active] || []).toSorted(sortByCreatedDateDesc),
      ...(byStatus[PricingSettingStatus.archived] || []).toSorted(sortByCreatedDateDesc),
    ];
  }, [data]);

  const handleArchive = async (id: string) => {
    try {
      await deletePropertyTypePricingMutation({ alias, id, programType }).unwrap();
      toast.success('Property Type pricing archived successfully');
    } catch (e) {
      console.error(e);
      toast.error('Error archiving Property Type pricing');
    }
  };

  const columnConfig: Column[] = [
    {
      heading: 'status',
      dataKey: 'status',
      render: (cell: any) => (
        <Badge
          variant={
            cell === PricingSettingStatus.active
              ? 'success'
              : cell === PricingSettingStatus.archived
                ? 'warning'
                : 'danger'
          }
          message={cell}
        />
      ),
    },
    {
      heading: 'Property Type',
      dataKey: 'propertyType',
      render: (cell: any) => propertyTypeLabelsMap[cell as PropertyTypeEnum] ?? cell,
    },
    {
      heading: 'Amount',
      dataKey: 'value',
      render: (cell: any) => formatPricePerWatt(cell?.pricePerWatt ?? 0),
    },
    { heading: 'org', dataKey: 'organizationName' },
    {
      heading: 'Created',
      dataKey: 'createdBy',
      width: 150,
      render: (cell: any) =>
        cell?.date ? <DateTimeDisplay value={cell.date} dateFormat={'DD'} excludeTime /> : null,
    },
    {
      heading: 'Archived',
      dataKey: 'archivedBy',
      width: 150,
      render: (cell: any) =>
        cell?.date ? <DateTimeDisplay value={cell.date} dateFormat={'DD'} excludeTime /> : null,
    },
    {
      heading: '',
      render: (_cell: any, row: any) => {
        const isOwnedByOrg = row.organizationId === alias;
        return (
          row.status === PricingSettingStatus.active &&
          isOwnedByOrg && (
            <RequirePricingAdminUser>
              <Button
                size="sm"
                variant="secondary"
                tone="neutral"
                onClick={() => {
                  handleArchive(row.id);
                }}
              >
                Archive
              </Button>
            </RequirePricingAdminUser>
          )
        );
      },
    },
  ];

  return (
    <>
      <CreatePropertyType isModalOpen={isModalOpen} closeModal={closeModal} />
      <Card.Header>
        <RequirePricingAdminUser>
          <Box direction="row" justifyContent="space-between">
            <Box />
            <Button
              as="button"
              variant="primary"
              iconPrefix="add"
              size={{ base: 'sm', desktop: 'md' }}
              aria-label="add property type pricing"
              onClick={() => openModal()}
            >
              {!isPhone && 'Property Type'}
            </Button>
          </Box>
        </RequirePricingAdminUser>
      </Card.Header>
      <Box margin="0 lg 0 lg">
        {isFetching || isLoading ? (
          <TableLoading rows={12} />
        ) : (
          <Table
            rowKey="id"
            columns={columnConfig}
            rows={propertyTypes ?? []}
            isLoading={isLoading || isFetching}
            isScrollable={{ y: true }}
            hasStickyHeader
          />
        )}
      </Box>
    </>
  );
};
